import React from 'react'
import { Box, Dialog, Typography } from '@mui/material'
import { Close as CloseIcon, Campaign as CampaignIcon } from '@mui/icons-material'
import { useCountdown } from '../../../hook/useCountdown'
import { Inline, parseParagraph, splitVideo, youtubeEmbedUrl } from './paragraphFormat'

/*
 * A pop-up campaign exactly as the user meets it in the new dashboard
 * (espy-dashboard container/PopupCampaign.tsx, the non-legacy branch): the
 * dotted blue gradient banner with the campaign name and close button, the
 * icon tile straddling its edge, the countdown chips, the blue title, the
 * paragraph and the full-width call-to-action — or, on a phone, the
 * half-height bottom sheet. Ported, not shared: the two apps share no code.
 *
 * It is drawn on a mock screen where the user would see it: in the
 * bottom-right corner, or centred over a dimmed page for a centre overlay.
 * Desktop / Phone and the user's light / dark theme can be switched. The
 * countdown is the real one (from createdTime for its duration); an ended
 * campaign shows what the user would — "Campaign is expired" — and can be
 * previewed as if it started now.
 *
 * The paragraph's formatting (paragraphFormat.ts — styles, lists, line
 * alignment, links, a promo code with Copy, a YouTube video above the
 * text, playing muted) is drawn as the dashboard draws it
 * (component/CampaignParagraph.tsx there).
 */

export type CampaignPreviewData = {
  name?: string
  title?: string
  paragraph?: string
  actionName?: string
  actionUrl?: string
  type?: string
  showCountdown?: boolean
  /** duration — the campaign's finalDuration / duration, or the form's fields */
  hours?: number | string
  minutes?: number | string
  /** when it (re)started; none = a campaign not saved yet, previewed as starting now */
  createdTime?: string | number | Date | null
}

// the dashboard's tokens (theme/searchTokens.ts) — the user's colours, not the admin's
const BRAND = { primary: '#2D89F4', primaryDark: '#1B6FD6' }
const TK = {
  light: { surface: '#FFFFFF', surface3: '#EFF4FC', border: '#E2E8F0', borderStrong: '#CBD5E1', text: '#0F172A', text2: '#64748B', page: '#F4F7FC' },
  dark: { surface: '#12112C', surface3: '#1E1D42', border: '#272654', borderStrong: '#3A3872', text: '#E9EDF9', text2: '#98A2C0', page: '#08071A' },
}
const R = { sm: 8, md: 12, lg: 16 }
const pad2 = (n: number) => String(Math.max(0, n)).padStart(2, '0')
const banner = {
  background: `linear-gradient(135deg, ${BRAND.primaryDark}, ${BRAND.primary})`,
  backgroundImage: `radial-gradient(rgba(255,255,255,.14) 1px, transparent 1px), linear-gradient(135deg, ${BRAND.primaryDark}, ${BRAND.primary})`,
  backgroundSize: '10px 10px, 100% 100%',
}

/** a promo code as the user gets it — tap Copy, see "Copied" */
function PromoCode({ code, phone }: { code: string; phone: boolean }) {
  const [copied, setCopied] = React.useState(false)
  const copy = () => {
    void navigator.clipboard?.writeText(code).catch(() => undefined)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1400)
  }
  return (
    <Box component="span" sx={{
      display: 'inline-flex', alignItems: 'center', gap: '6px', verticalAlign: 'middle', m: '1px 2px', p: '1px 2px 1px 8px',
      borderRadius: '7px', border: `1px dashed ${BRAND.primary}`, background: 'rgba(45,137,244,.1)', whiteSpace: 'nowrap',
    }}>
      <Box component="span" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 700, letterSpacing: '.04em', color: BRAND.primary, fontSize: phone ? 10.5 : 12 }}>{code}</Box>
      <Box component="button" type="button" onClick={copy} sx={{
        border: 0, cursor: 'pointer', borderRadius: '5px', background: BRAND.primary, color: '#fff', font: 'inherit',
        fontSize: phone ? 9.5 : 10.5, fontWeight: 700, lineHeight: 1, p: phone ? '4px 6px' : '5px 7px',
      }}>{copied ? 'Copied ✓' : 'Copy'}</Box>
    </Box>
  )
}

function InlineNodes({ nodes, phone }: { nodes: Inline[]; phone: boolean }) {
  return (
    <>
      {nodes.map((n, i) => {
        if (n.t === 'text') return <React.Fragment key={i}>{n.v}</React.Fragment>
        if (n.t === 'link') return <a key={i} href={n.href} target="_blank" rel="noopener noreferrer" style={{ color: BRAND.primary, textDecoration: 'underline', textUnderlineOffset: 2 }}>{n.v}</a>
        if (n.t === 'code') return <PromoCode key={i} code={n.v} phone={phone} />
        const inner = <InlineNodes nodes={n.c} phone={phone} />
        if (n.t === 'b') return <strong key={i} style={{ fontWeight: 700 }}>{inner}</strong>
        if (n.t === 'i') return <em key={i}>{inner}</em>
        if (n.t === 's') return <s key={i}>{inner}</s>
        return <span key={i} style={{ color: BRAND.primary, fontWeight: 600 }}>{inner}</span>
      })}
    </>
  )
}

/** the paragraph with its markup (paragraphFormat.ts) drawn — what the dashboard should draw too */
export function Paragraph({ value, phone }: { value?: string; phone: boolean }) {
  const blocks = parseParagraph(value || '')
  return (
    <>
      {blocks.map((b, i) => (b.t === 'list'
        ? (
          <ul key={i} style={{ margin: '4px 0', paddingLeft: phone ? 16 : 18 }}>
            {b.items.map((it, j) => <li key={j}><InlineNodes nodes={it} phone={phone} /></li>)}
          </ul>
        )
        : (
          <div key={i} style={{ textAlign: b.align || undefined }}>
            {b.c.length ? <InlineNodes nodes={b.c} phone={phone} /> : <br />}
          </div>
        )))}
    </>
  )
}

/** the campaign's YouTube video, 16:9 — it starts by itself without sound, the player has the sound button */
function Video({ id, phone }: { id: string; phone: boolean }) {
  return (
    <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', borderRadius: `${phone ? R.sm : R.md}px`, overflow: 'hidden', background: '#000', mb: phone ? '8px' : '10px' }}>
      <iframe
        src={youtubeEmbedUrl(id)}
        title="Campaign video"
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
        allowFullScreen
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
      />
    </Box>
  )
}

function CloseDot({ size }: { size: number }) {
  return (
    <Box sx={{ flex: 'none', width: size, height: size, borderRadius: '50%', background: '#fff', color: '#1B1C2E', display: 'grid', placeItems: 'center', boxShadow: '0 2px 6px rgba(0,0,0,.3)' }}>
      <CloseIcon sx={{ fontSize: size * 0.6 }} />
    </Box>
  )
}

function Popup({ d, tk, phone, chips, expired }: {
  d: CampaignPreviewData; tk: typeof TK.light; phone: boolean; chips: { v: string; l: string }[]; expired: boolean
}) {
  const video = splitVideo(d.paragraph || '').video
  const button = (
    <Box sx={{ mt: phone ? 0 : '12px', borderRadius: `${R.sm}px`, background: BRAND.primary, color: '#fff', textAlign: 'center', fontSize: phone ? 11 : 11.5, fontWeight: 700, py: phone ? '8px' : '9px' }}>
      {d.actionName || 'Call to action'}
    </Box>
  )
  if (phone) {
    // the half-height bottom sheet
    return (
      <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '50%', display: 'flex', flexDirection: 'column', background: tk.surface, borderTop: `1px solid ${tk.border}`, borderRadius: `${R.lg}px ${R.lg}px 0 0`, boxShadow: '0 -10px 30px -10px rgba(0,0,0,.6)', overflow: 'hidden' }}>
        <Box sx={{ width: 32, height: 4, borderRadius: '4px', background: tk.borderStrong, margin: '7px auto 0', flex: 'none' }} />
        <Box sx={{ ...banner, margin: '6px 10px 0', borderRadius: `${R.md}px`, padding: '8px 10px', display: 'flex', alignItems: 'center', gap: '8px', flex: 'none' }}>
          <Box sx={{ width: 28, height: 28, borderRadius: `${R.sm}px`, background: 'rgba(255,255,255,.16)', display: 'grid', placeItems: 'center', color: '#fff', flex: 'none' }}>
            <CampaignIcon sx={{ fontSize: 15 }} />
          </Box>
          <Typography sx={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, minWidth: 0 }}>{d.name}</Typography>
          <CloseDot size={18} />
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 14px 6px' }}>
          {d.title && <Typography sx={{ display: 'block', fontSize: 12, fontWeight: 700, color: BRAND.primary, mb: '5px' }}>{d.title}</Typography>}
          {expired ? (
            <Typography sx={{ display: 'block', fontSize: 11, color: tk.text2 }}>Campaign is expired</Typography>
          ) : (
            <>
              {video && <Video id={video} phone />}
              <Typography component="div" sx={{ display: 'block', fontSize: 11, lineHeight: 1.55, color: tk.text2, mb: '8px', whiteSpace: 'pre-wrap' }}><Paragraph value={d.paragraph} phone /></Typography>
              {d.showCountdown && (
                <Box sx={{ display: 'flex', gap: '4px' }}>
                  {chips.map((c) => (
                    <Box key={c.l} sx={{ background: tk.surface3, borderRadius: `${R.sm}px`, padding: '4px 6px', display: 'flex', alignItems: 'baseline', gap: '2px' }}>
                      <Typography sx={{ display: 'inline', fontSize: 10, fontWeight: 700, color: tk.text }}>{c.v}</Typography>
                      <Typography sx={{ display: 'inline', fontSize: 7, fontWeight: 700, color: BRAND.primary }}>{c.l}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>
        {!expired && <Box sx={{ flex: 'none', borderTop: `1px solid ${tk.border}`, padding: '8px 14px' }}>{button}</Box>}
      </Box>
    )
  }
  // desktop card
  return (
    <Box sx={{ width: 442, maxWidth: 'calc(100% - 32px)', display: 'flex', flexDirection: 'column', background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: `${R.lg}px`, boxShadow: '0 20px 50px -20px rgba(0,0,0,.7)' }}>
      <Box sx={{ position: 'relative' }}>
        <Box sx={{ ...banner, padding: '14px 16px 30px', borderRadius: `${R.lg}px ${R.lg}px 0 0`, display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Typography sx={{ display: 'block', fontSize: 18, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, minWidth: 0 }}>{d.name}</Typography>
          <CloseDot size={22} />
        </Box>
        <Box sx={{ width: 46, height: 46, borderRadius: `${R.md}px`, background: tk.surface, position: 'absolute', top: 44, left: 16, display: 'grid', placeItems: 'center', color: BRAND.primary, boxShadow: '0 8px 18px -6px rgba(0,0,0,.55)' }}>
          <CampaignIcon sx={{ fontSize: 21 }} />
        </Box>
        {!expired && d.showCountdown && (
          <Box sx={{ position: 'absolute', top: 53, right: 16, display: 'flex', gap: '5px' }}>
            {chips.map((c) => (
              <Box key={c.l} sx={{ background: tk.surface, borderRadius: `${R.sm}px`, padding: '5px 7px', display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '2px', minWidth: 28, boxShadow: '0 8px 18px -6px rgba(0,0,0,.55)' }}>
                <Typography sx={{ display: 'inline', fontSize: 11, fontWeight: 700, color: tk.text }}>{c.v}</Typography>
                <Typography sx={{ display: 'inline', fontSize: 7, fontWeight: 700, color: BRAND.primary }}>{c.l}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
      <Box sx={{ padding: '28px 16px 16px', minWidth: 0 }}>
        {d.title && <Typography sx={{ display: 'block', fontSize: 15, fontWeight: 700, color: BRAND.primary, mb: '6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.title}</Typography>}
        {expired ? (
          <Typography sx={{ display: 'block', fontSize: 12, color: tk.text2 }}>Campaign is expired</Typography>
        ) : (
          <>
            {video && <Video id={video} phone={false} />}
            <Typography component="div" sx={{ display: 'block', fontSize: 13, lineHeight: 1.6, color: tk.text2, whiteSpace: 'pre-wrap' }}><Paragraph value={d.paragraph} phone={false} /></Typography>
            {button}
          </>
        )}
      </Box>
    </Box>
  )
}

/**
 * The user's screen with the pop-up on it — a faint dashboard behind, the
 * pop-up in its corner or centred over a dimmed page, or the phone sheet.
 * `start` is when the countdown began (default: now); `size` scales the
 * screen down for narrow places like the create dialog.
 */
export function CampaignScreen({ data, mode, device, start: startAt, size }: {
  data: CampaignPreviewData
  mode: 'light' | 'dark'
  device: 'desktop' | 'phone'
  start?: number
  size?: { width: number | string; height: number; phoneWidth?: number; phoneHeight?: number }
}) {
  const [now] = React.useState(() => Date.now())
  const start = startAt ?? now
  const seconds = (Number(data.hours) || 0) * 3600 + (Number(data.minutes) || 0) * 60
  const cd = useCountdown(start + seconds * 1000, true)
  const expired = !!cd.isExpired
  const chips = [
    { v: pad2(cd.days), l: 'D' }, { v: pad2(cd.hours), l: 'H' }, { v: pad2(cd.minutes), l: 'M' }, { v: pad2(cd.seconds), l: 'S' },
  ]
  const tk = TK[mode]
  const phone = device === 'phone'
  const center = data.type === 'center-overlay'
  const width = phone ? size?.phoneWidth ?? 360 : size?.width ?? 'min(900px, calc(100vw - 100px))'
  const height = phone ? size?.phoneHeight ?? 640 : size?.height ?? 520

  return (
        <Box sx={{
          position: 'relative', overflow: 'hidden', borderRadius: phone ? '28px' : '14px',
          width, height,
          background: tk.page, border: `1px solid ${tk.border}`, boxShadow: '0 10px 30px -15px rgba(15,23,42,.4)',
        }}>
          {/* a faint dashboard behind */}
          <Box sx={{ position: 'absolute', inset: 0, p: '16px', display: 'flex', flexDirection: 'column', gap: '10px', opacity: 0.55 }}>
            <Box sx={{ height: 34, borderRadius: '10px', background: tk.surface, border: `1px solid ${tk.border}` }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: phone ? '1fr' : 'repeat(3, 1fr)', gap: '10px' }}>
              {[0, 1, 2].map((i) => <Box key={i} sx={{ height: 70, borderRadius: '12px', background: tk.surface, border: `1px solid ${tk.border}` }} />)}
            </Box>
            <Box sx={{ flex: 1, borderRadius: '12px', background: tk.surface, border: `1px solid ${tk.border}` }} />
          </Box>
          {(phone || center) && <Box sx={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.45)' }} />}
          {phone ? (
            <Popup d={data} tk={tk} phone chips={chips} expired={expired} />
          ) : (
            <Box sx={{
              position: 'absolute',
              ...(center ? { inset: 0, display: 'grid', placeItems: 'center' } : { right: 16, bottom: 16 }),
            }}>
              <Popup d={data} tk={tk} phone={false} chips={chips} expired={expired} />
            </Box>
          )}
        </Box>
  )
}

export function CampaignPreviewV2({ data, onClose, adminTheme = 'light' }: {
  data: CampaignPreviewData
  onClose: () => void
  /** the admin page's theme — the preview starts in the same one */
  adminTheme?: 'light' | 'dark'
}) {
  const [device, setDevice] = React.useState<'desktop' | 'phone'>('desktop')
  const [mode, setMode] = React.useState<'light' | 'dark'>(adminTheme)
  const seconds = (Number(data.hours) || 0) * 3600 + (Number(data.minutes) || 0) * 60
  const realStart = data.createdTime ? new Date(data.createdTime).getTime() : NaN
  const [fromNow, setFromNow] = React.useState(Number.isNaN(realStart))
  const [now] = React.useState(() => Date.now())
  const start = fromNow || Number.isNaN(realStart) ? now : realStart
  const cd = useCountdown(start + seconds * 1000, true)
  const expired = !!cd.isExpired
  const center = data.type === 'center-overlay'

  const seg = (value: string, current: string, set: (v: any) => void, label: string) => (
    <button
      type="button"
      onClick={() => set(value)}
      style={{
        height: 30, padding: '0 12px', borderRadius: 8, border: 0, cursor: 'pointer', font: 'inherit', fontSize: 12.5, fontWeight: 600,
        background: current === value ? '#fff' : 'transparent', color: current === value ? '#0F172A' : '#64748B',
        boxShadow: current === value ? '0 1px 2px rgba(15,23,42,.1)' : 'none',
      }}
    >{label}</button>
  )

  return (
    <Dialog open onClose={onClose} maxWidth={false} PaperProps={{ sx: { borderRadius: '18px', m: 2, background: '#fff', overflow: 'hidden' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', p: '14px 18px', borderBottom: '1px solid #E2E8F0', flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: BRAND.primary }}>Preview</Typography>
          <Typography sx={{ display: 'block', fontSize: 15, fontWeight: 700, color: '#0F172A' }}>How users see “{data.name || 'this campaign'}”</Typography>
        </Box>
        <Box sx={{ display: 'inline-flex', p: '3px', gap: '2px', borderRadius: '10px', background: '#EFF4FC' }}>
          {seg('desktop', device, setDevice, 'Desktop')}
          {seg('phone', device, setDevice, 'Phone')}
        </Box>
        <Box sx={{ display: 'inline-flex', p: '3px', gap: '2px', borderRadius: '10px', background: '#EFF4FC' }}>
          {seg('light', mode, setMode, 'Light')}
          {seg('dark', mode, setMode, 'Dark')}
        </Box>
        <button type="button" onClick={onClose} aria-label="Close" style={{ width: 32, height: 32, borderRadius: 10, border: '1px solid #E2E8F0', background: '#fff', cursor: 'pointer', display: 'grid', placeItems: 'center', color: '#64748B' }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </button>
      </Box>

      <Box sx={{ p: '18px', background: '#F1F5F9', display: 'flex', justifyContent: 'center' }}>
        <CampaignScreen data={data} mode={mode} device={device} start={start} />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: '10px 18px', borderTop: '1px solid #E2E8F0', fontSize: 12.5, color: '#64748B', flexWrap: 'wrap' }}>
        <span>{center ? 'Centre overlay' : 'Bottom-right corner'}{data.showCountdown ? ' · with countdown' : ''}</span>
        {!Number.isNaN(realStart) && (
          <label style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={fromNow} onChange={(e) => setFromNow(e.target.checked)} />
            Preview as if started now
          </label>
        )}
        {!fromNow && expired && <span style={{ color: '#EF4444', fontWeight: 600 }}>Ended — users see “Campaign is expired”</span>}
      </Box>
    </Dialog>
  )
}
